import {
  useDisclosure,
  Spinner,
  Flex,
  Text,
  Badge,
  Checkbox,
} from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';

import { useGetTapeBuilderLatestPipelineRunQuery } from '../../requests/tapeBuilder.gql.generated';
import { CalendarModal } from './CalendarModal';
import { DateAndVersionInput } from './DateAndVersionInput';
import { SelectedPipeline } from './SelectedPipeline';

// displays selected pipeline, and allows user to pick existing pipeline.
// provides a shortcut to quickly select latest pipeline
// monitors for any latest pipeline changes
export const TapeBuilderPipelinePicker = ({
  pipelineDefinitionId,
  loadArtifactInstances,
}: {
  pipelineDefinitionId: string;
  loadArtifactInstances: (pipelineRunId: string) => void;
}) => {
  const [pollForUpdates, setPollForUpdates] = useState<boolean>(false);
  const [
    selectedPipeline,
    setSelectedPipeline,
  ] = useState<SelectedPipeline | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    data: latestData,
    loading: latestLoading,
    error: latestError,
  } = useGetTapeBuilderLatestPipelineRunQuery({
    variables: {
      pipelineDefinitionId,
    },
    fetchPolicy: 'network-only',
    pollInterval: pollForUpdates ? 2000 : 0,
  });

  // when selectedPipeline changes, load artifact instances
  const selectedRunId = selectedPipeline?.pipelineRunId;
  useEffect(() => {
    if (selectedRunId) {
      loadArtifactInstances(selectedRunId);
    }
  }, [selectedRunId, loadArtifactInstances]);

  // when polling for updates load artifact instances
  // of selected pipeline at regular intervals
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (pollForUpdates && selectedRunId) {
        loadArtifactInstances(selectedRunId);
      }
    }, 2000);
    return () => clearInterval(intervalId);
  }, [pollForUpdates, selectedRunId, loadArtifactInstances]);

  const latestRun = latestData?.getPipelineRuns?.[0];
  useEffect(() => {
    // if no pipeline is selected, and latest pipeline is available
    // set it as selected pipeline
    if (!selectedPipeline && latestRun) {
      setSelectedPipeline(latestRun);
    }
  }, [latestRun, selectedPipeline]);

  if (!latestData) {
    if (latestLoading) {
      return (
        <Flex justifyContent="center" alignItems="center" height="100%">
          <Spinner />
          <Text ml={2}>Loading latest pipeline...</Text>
        </Flex>
      );
    }

    if (latestError) {
      return (
        <Flex justifyContent="center" alignItems="center" height="100%">
          <Text color="red.500">Error loading latest pipeline</Text>
        </Flex>
      );
    }

    return (
      <Flex justifyContent="center" alignItems="center" height="100%">
        <Text color="red.500">Latest data not available</Text>
      </Flex>
    );
  }

  // if selected pipeline is not latest pipeline - show a badge that lets you select latest pipeline
  let latestBadgePipeline: SelectedPipeline | null = null;
  if (
    latestData.getPipelineRuns &&
    (!selectedPipeline ||
      latestData.getPipelineRuns[0].pipelineRunId !==
        selectedPipeline.pipelineRunId)
  ) {
    latestBadgePipeline = latestData.getPipelineRuns[0];
  }

  return (
    <>
      <DateAndVersionInput selectedPipeline={selectedPipeline} onClick={onOpen}>
        {latestBadgePipeline && (
          <Badge
            ml={2}
            colorScheme="yellow"
            variant="outline"
            onClick={(event) => {
              // you can navigate to current pipeline by clicking on badge
              event.stopPropagation();
              setSelectedPipeline(latestBadgePipeline);
            }}
          >
            Goto latest
          </Badge>
        )}
      </DateAndVersionInput>
      {isOpen && (
        <CalendarModal
          selectedPipeline={selectedPipeline}
          setSelectedPipeline={setSelectedPipeline}
          onClose={onClose}
          pipelineDefinitionId={pipelineDefinitionId}
        />
      )}
      <Checkbox
        ml="2"
        isChecked={pollForUpdates}
        onChange={(e) => setPollForUpdates(e.target.checked)}
      >
        Auto-poll
      </Checkbox>
    </>
  );
};
